import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useOverlayContext } from 'seagull';
import { AddContactModal } from '~/components/AddContactModal/AddContactModal';
import { EditContactModal } from '~/components/EditContactModal/EditContactModal';
import { useAccountingCustomers } from '~/hooks/reactQuery/queries/useAccountingCustomers';
import { useClientContacts } from '~/hooks/reactQuery/queries/useClientContacts';
import { useIntegrationsSummary } from '~/hooks/reactQuery/queries/useIntegrationsSummary';
import { QueryKeys } from '~/hooks/reactQuery/queryKeys';
import { useContactArchiveRestoreDelete } from '~/hooks/useContactArchiveRestoreDelete';
import { selectors } from '~/store/state-selectors';
import { useStore } from '~/store/store-context';
import { Accounting } from '~/types/accounting';
import { buildCustomerIdToDisplay, toContactCardContacts } from '../ContactsCardOld.utils';
export function useContactsCardOld(clientId, isArchived) {
    const { contacts: allModels, isLoading, error, refresh } = useClientContacts(clientId, {
        refreshOnHookMount: true
    });
    const models = useMemo(()=>isArchived ? allModels : allModels.filter((model)=>model.isActive()), [
        allModels,
        isArchived
    ]);
    const { showModal } = useOverlayContext();
    const queryClient = useQueryClient();
    const refreshContactsAndClients = async ()=>{
        await refresh();
        queryClient.invalidateQueries({
            queryKey: [
                QueryKeys.Clients
            ]
        });
        queryClient.invalidateQueries({
            queryKey: [
                QueryKeys.ClientSummaries
            ]
        });
    };
    const { onDelete } = useContactArchiveRestoreDelete({
        onSuccess: refreshContactsAndClients
    });
    const showQbo = useStore(selectors.integration.showQboIntegration);
    const showXero = useStore(selectors.integration.showXeroIntegration);
    useIntegrationsSummary({
        enabled: showQbo || showXero,
        refreshOnHookMount: true
    });
    const { customers: qboCustomers } = useAccountingCustomers(Accounting.QBO, {
        enabled: showQbo,
        refreshOnHookMount: true
    });
    const { customers: xeroCustomers } = useAccountingCustomers(Accounting.Xero, {
        enabled: showXero,
        refreshOnHookMount: true
    });
    const contacts = useMemo(()=>toContactCardContacts(models, {
            showQbo,
            showXero,
            qboCustomerIdToDisplay: buildCustomerIdToDisplay(qboCustomers),
            xeroCustomerIdToDisplay: buildCustomerIdToDisplay(xeroCustomers)
        }), [
        models,
        showQbo,
        showXero,
        qboCustomers,
        xeroCustomers
    ]);
    const onAddContact = ()=>showModal({
            biName: 'client_detail_add_contact',
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(AddContactModal, {
                    clientId: clientId,
                    dismissModal: dismissModal
                });
            }
        });
    const onEditContact = async (contactId)=>{
        const contact = models.find((model)=>model.id() === contactId);
        if (contact == null) return;
        await showModal({
            biName: 'client_detail_edit_contact',
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(EditContactModal, {
                    clientId: clientId,
                    contact: contact,
                    dismissModal: dismissModal
                });
            }
        });
        refresh();
    };
    const withContact = (contactId, action)=>{
        const contact = models.find((model)=>model.id() === contactId);
        if (contact != null) action(contact);
    };
    return {
        contacts,
        models,
        isLoading,
        isError: error != null,
        onAddContact,
        onEditContact,
        onDeleteContact: (contactId)=>withContact(contactId, onDelete)
    };
}
